import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { 
  GitCompare, 
  FileText, 
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

import { API_URL } from '../config';

const MONTHS = [
  'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
  'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'
];

export default function ComparePage() {
  const [payslips, setPayslips] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPayslips();
  }, []);

  const fetchPayslips = async () => {
    try {
      const response = await axios.get(`${API_URL}/payslips`);
      setPayslips(response.data);
    } catch (err) {
      console.error('Error fetching payslips:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      }
      if (prev.length >= 6) {
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleCompare = async () => {
    if (selectedIds.length < 2) {
      setError('Seleziona almeno 2 buste paga da confrontare');
      return;
    }

    setComparing(true);
    setError('');

    try {
      const response = await axios.get(`${API_URL}/compare?payslip_ids=${selectedIds.join(',')}`);
      setComparison(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel confronto');
    } finally {
      setComparing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatPercent = (value) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const getTrendIcon = (value) => {
    if (value > 0) return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (value < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const chartData = comparison?.payslips?.map(p => ({
    period: `${MONTHS[p.data.period_month - 1]} ${p.data.period_year}`,
    Lordo: p.data.gross_salary,
    Netto: p.data.net_salary,
    Trattenute: p.data.gross_salary - p.data.net_salary
  })) || [];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="compare-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Confronta Buste Paga</h1>
          <p className="text-muted-foreground mt-1">
            Seleziona fino a 6 buste paga per confrontarle
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Selection List */}
          <Card className="swiss-card lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Seleziona Buste Paga</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="spinner" />
                </div>
              ) : payslips.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-4">Nessuna busta paga disponibile</p>
                  <Button asChild className="swiss-btn-primary" size="sm">
                    <Link to="/upload">Carica ora</Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {payslips.map((p) => (
                    <label
                      key={p.id}
                      className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${
                        selectedIds.includes(p.id) 
                          ? 'border-forest bg-mint' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`select-payslip-${p.id}`}
                    >
                      <Checkbox
                        checked={selectedIds.includes(p.id)}
                        onCheckedChange={() => handleSelect(p.id)}
                        disabled={!selectedIds.includes(p.id) && selectedIds.length >= 6}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">
                          {MONTHS[p.period_month - 1]} {p.period_year}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {p.company_name}
                        </div>
                      </div>
                      <div className="font-mono text-sm text-forest">
                        {formatCurrency(p.net_salary)}
                      </div>
                    </label>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground mb-3">
                  Selezionate: {selectedIds.length}/6
                </div>
                <Button
                  className="swiss-btn-primary w-full"
                  onClick={handleCompare}
                  disabled={selectedIds.length < 2 || comparing}
                  data-testid="compare-btn"
                >
                  {comparing ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner" />
                      Confronto...
                    </span>
                  ) : (
                    <>
                      <GitCompare className="w-4 h-4 mr-2" />
                      Confronta ({selectedIds.length})
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Comparison Results */}
          <div className="lg:col-span-2 space-y-6">
            {comparison ? (
              <>
                {/* Chart */}
                <Card className="swiss-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Andamento Retribuzione</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="period" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                          <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Bar dataKey="Lordo" fill="#064e3b" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="Netto" fill="#10b981" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="Trattenute" fill="#ea580c" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Differences */}
                {comparison.differences.map((diff, index) => (
                  <Card key={index} className="swiss-card" data-testid={`diff-${index}`}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="text-muted-foreground">{diff.from_period}</span>
                        <ArrowRight className="w-4 h-4" />
                        <span>{diff.to_period}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-slate-50 rounded-sm">
                          <div className="label-uppercase mb-1">Lordo</div>
                          <div className="flex items-center gap-2">
                            {getTrendIcon(diff.gross_change)}
                            <span className="font-mono text-sm">
                              {diff.gross_change > 0 ? '+' : ''}{formatCurrency(diff.gross_change)}
                            </span>
                          </div>
                          <div className={`text-xs mt-1 ${diff.gross_change_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatPercent(diff.gross_change_pct)}
                          </div>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-sm">
                          <div className="label-uppercase mb-1">Netto</div>
                          <div className="flex items-center gap-2">
                            {getTrendIcon(diff.net_change)}
                            <span className="font-mono text-sm">
                              {diff.net_change > 0 ? '+' : ''}{formatCurrency(diff.net_change)}
                            </span>
                          </div>
                          <div className={`text-xs mt-1 ${diff.net_change_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatPercent(diff.net_change_pct)}
                          </div>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-sm">
                          <div className="label-uppercase mb-1">IRPEF</div>
                          <div className="flex items-center gap-2">
                            {getTrendIcon(-diff.irpef_change)}
                            <span className="font-mono text-sm">
                              {diff.irpef_change > 0 ? '+' : ''}{formatCurrency(diff.irpef_change)}
                            </span>
                          </div>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-sm">
                          <div className="label-uppercase mb-1">INPS</div>
                          <div className="flex items-center gap-2">
                            {getTrendIcon(-diff.inps_change)}
                            <span className="font-mono text-sm">
                              {diff.inps_change > 0 ? '+' : ''}{formatCurrency(diff.inps_change)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Summary Table */}
                <Card className="swiss-card overflow-hidden">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Riepilogo Dettagliato</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="payslip-table">
                        <thead>
                          <tr className="bg-slate-50">
                            <th>Periodo</th>
                            <th className="text-right">Lordo</th>
                            <th className="text-right">INPS</th>
                            <th className="text-right">IRPEF</th>
                            <th className="text-right">Netto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {comparison.payslips.map((p) => (
                            <tr key={p.id}>
                              <td className="font-medium">
                                {MONTHS[p.data.period_month - 1]} {p.data.period_year}
                              </td>
                              <td className="text-right font-mono">{formatCurrency(p.data.gross_salary)}</td>
                              <td className="text-right font-mono text-red-600">-{formatCurrency(p.data.inps_employee)}</td>
                              <td className="text-right font-mono text-red-600">-{formatCurrency(p.data.irpef)}</td>
                              <td className="text-right font-mono font-semibold text-forest">{formatCurrency(p.data.net_salary)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="swiss-card">
                <CardContent className="p-12">
                  <div className="text-center">
                    <GitCompare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Seleziona le buste paga</h3>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      Scegli almeno 2 buste paga dalla lista a sinistra per visualizzare il confronto dettagliato.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
